import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { catalog, scopeKey } from '../api/endpoints.ts';
import type { MyAddress, OrderListItem, StorefrontItem } from '../api/types.ts';
import { formatPara, para, ratio } from '../core/money.ts';
import { formatWeight, totalNutrition } from '../core/nutrition.ts';
import { useStore } from '../core/store.ts';
import { errorText as textOf, orderMoment, useLang, useStrings, type Strings } from '../i18n/index.ts';
import {
  addressDetailsLine,
  etaText,
  GUEST_ADDRESS_ID,
  removeAddress,
  selectAndRefresh,
  selectedAddress,
  selectedOutOfZone,
  setGuest,
  storefrontScope,
  tariffOf,
  useAddressBook,
  deliveryFee,
  missingTo,
} from '../state/address-book.ts';
import {
  applyCartChanges,
  availableTotal,
  availableWeightG,
  canAdd,
  cartStore,
  clearCart,
  decreaseInCart,
  hasGoneLines,
  isGone,
  lineTotal,
  overweight,
  pendingChanges,
  removeFromCart,
  syncCartWith,
  useCart,
  type CartLine,
  type CartState,
} from '../state/cart.ts';
import { addWithChecks, checkCart } from '../state/cart-sync.ts';
import { ageGateStore, dismissAgeGate } from '../state/gates.ts';
import { lookupProduct } from '../state/reorder.ts';
import { openSignIn, pushLayer, useRouter } from '../state/router.ts';
import { useSession } from '../state/session.ts';
import { isStorePaused, localClock, storeEtaText, storeInfoStore } from '../state/stores.ts';
import { notify } from '../state/toasts.ts';
import { useWatcher } from '../state/watcher.ts';
import { Icon } from '../ui/icons.tsx';
import { BarButton, Button, Card, cx, EmptyState, ErrorBanner, ProgressBar, QuantityStepper, RemoteImage, ScreenTitle, Skeleton, Spinner } from '../ui/kit.tsx';
import { NutritionSummary } from '../ui/NutritionSummary.tsx';
import { ProductNavContext, ProductTile } from '../ui/ProductTile.tsx';
import { Screen, ScreenPlaceContext } from '../ui/Screen.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { AgeGateSheet } from './AgeGateSheet.tsx';
import { Checkout } from './Checkout.tsx';
import { NewAddressScreen } from './NewAddressScreen.tsx';
import { ProductScreen } from './ProductScreen.tsx';
import { paymentConfigStore } from '../state/payment.ts';
import { SupplementSheet } from './SupplementSheet.tsx';
import '../styles/cart.css';

/** Лист корзины: корзина, оформление и выбор адреса шагами одного листа. */
export function CartSheet(props: { onClose: () => void }) {
  const strings = useStrings();
  const [busy, setBusy] = useState(false);
  return (
    <Sheet size="large" page grabber={false} locked={busy} label={strings.cartTitle} onClose={props.onClose}>
      {(close) => <CartFlow close={close} onBusy={setBusy} />}
    </Sheet>
  );
}

type Step = { kind: 'checkout' } | { kind: 'addresses' } | { kind: 'newAddress' } | { kind: 'product'; item: StorefrontItem };
type StepEntry = { step: Step; release: () => void };

/**
 * Шаги поверх корзины. У каждого шага своя запись истории: «назад» браузера возвращает на
 * шаг, а не закрывает лист.
 */
function useSteps() {
  const [steps, setSteps] = useState<StepEntry[]>([]);
  const current = useRef(steps);
  current.current = steps;

  const push = useCallback((step: Step) => {
    const entry: StepEntry = {
      step,
      release: pushLayer(() => setSteps((items) => items.filter((item) => item !== entry))),
    };
    setSteps((items) => [...items, entry]);
  }, []);

  const pop = useCallback(() => {
    const top = current.current.at(-1);
    if (!top) return;
    setSteps((items) => items.filter((item) => item !== top));
    top.release();
  }, []);

  // Лист закрыли целиком: записи истории оставшихся шагов уходят вместе с ним.
  useEffect(() => () => current.current.forEach((entry) => entry.release()), []);

  const popAll = useCallback(() => {
    for (const entry of [...current.current].reverse()) entry.release();
    setSteps([]);
  }, []);

  return { steps: steps.map((entry) => entry.step), push, pop, popAll };
}

/**
 * Корзина: состав, прогресс до бесплатной доставки, итоги и переход к оформлению.
 *
 * Суммы здесь только для показа: доставку и минимум заказа считает сервер при оформлении.
 * При открытии сверяется наличие всех строк. Разошедшийся товар не становится ошибкой: строка
 * гаснет и подписывается, а «Оформить» на это время заменяет кнопка «Обновить корзину».
 * Закрытый магазин запрещает только оформление.
 */
function CartFlow({ close, onBusy }: { close: () => void; onBusy: (busy: boolean) => void }) {
  const strings = useStrings();
  const lang = useLang();
  const session = useSession();
  const book = useAddressBook();
  const cart = useCart();
  const storeInfo = useStore(storeInfoStore);
  const watcher = useWatcher();
  const ageGate = useStore(ageGateStore);
  const paymentConfig = useStore(paymentConfigStore);
  const router = useRouter();
  const { steps, push, pop, popAll } = useSteps();

  /** Корзина уходит в заказ, который ещё собирают, вместо нового оформления. */
  const [supplementOrder, setSupplementOrder] = useState<OrderListItem | null>(null);
  /** Гость нажал «Оформить»: сначала вход, оформление откроется само, когда адрес ляжет в аккаунт. */
  const [pendingCheckout, setPendingCheckout] = useState(false);
  /** «Ничего не забыли?»: что берут с товарами корзины, без самих этих товаров. */
  const [recommended, setRecommended] = useState<StorefrontItem[]>([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);

  const signedIn = session.profile !== null;
  const scope = storefrontScope(book);
  const address = selectedAddress(book);
  const tariff = tariffOf(book);
  const outOfZone = selectedOutOfZone(book);
  const isEmpty = cart.lines.length === 0;

  const available = availableTotal(cart);
  const hasAvailableLines = available > 0n;
  const fee = deliveryFee(tariff, available);
  const total = available + fee;

  // Человек ждёт актуальные остатки: сверка при открытии, молча. О правках говорят сами
  // строки. Строки, сохранённые до появления веса и состава, дополняются с витрины.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await checkCart(cartStore.get().lines.map((line) => line.productId));
      const missing = cartStore.get().lines.filter((line) => line.netWeightG === null && line.nutrition === null).map((line) => line.productId);
      if (cancelled || missing.length === 0) return;
      const items = await catalog.byIds(missing, scope, lang).catch(() => []);
      if (!cancelled) syncCartWith(items);
    })();
    return () => {
      cancelled = true;
    };
    // Сверка идёт один раз на открытие листа.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Состав без количества: рекомендации перечитываются, когда меняется набор товаров.
  const recommendationsKey = `${cart.lines.map((line) => line.productId).sort().join(',')}|${scopeKey(scope)}|${lang}`;
  const hasRecommended = recommended.length > 0;
  useEffect(() => {
    const ids = cartStore.get().lines.map((line) => line.productId);
    if (ids.length === 0) {
      setRecommended([]);
      setRecommendationsLoading(false);
      return;
    }
    if (!hasRecommended) setRecommendationsLoading(true);
    let cancelled = false;
    // Пауза: плюс и минус в корзине не должны дёргать сервер на каждое нажатие.
    const timer = setTimeout(() => {
      catalog
        .recommendations(ids, scope, lang)
        .then((response) => {
          if (!cancelled) setRecommended(response.items);
        })
        .catch(() => undefined)
        .finally(() => {
          if (!cancelled) setRecommendationsLoading(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendationsKey]);

  const openNewAddress = useCallback(() => push({ kind: 'newAddress' }), [push]);

  // Вход из корзины продолжается оформлением, как только адрес гостя сохранён в аккаунт.
  // У аккаунта без адресов следующим шагом идёт новый адрес.
  useEffect(() => {
    if (!pendingCheckout || !signedIn || book.isLoading) return;
    if (scope.kind === 'address') {
      setPendingCheckout(false);
      push({ kind: 'checkout' });
    } else if (book.guest === null) {
      setPendingCheckout(false);
      openNewAddress();
    }
  }, [pendingCheckout, signedIn, book.isLoading, book.guest, scope, push, openNewAddress]);

  // Лист входа закрыли без входа: оформление больше не ждёт.
  const signInOpen = router.sheets.includes('signIn');
  const wasSignInOpen = useRef(signInOpen);
  useEffect(() => {
    if (wasSignInOpen.current && !signInOpen && !signedIn) setPendingCheckout(false);
    wasSignInOpen.current = signInOpen;
  }, [signInOpen, signedIn]);

  /** Карточка товара открывается шагом листа; её «В корзину» возвращает к самой корзине. */
  const openItem = useCallback((item: StorefrontItem) => push({ kind: 'product', item }), [push]);
  const productNav = useMemo(() => ({ open: openItem, goToCart: popAll }), [openItem, popAll]);

  /** Строка знает только идентификатор: товар читается с витрины склада адреса. */
  const openLine = (line: CartLine) => {
    void lookupProduct(line.productId).then((item) => {
      if (item) openItem(item);
      else notify(`${line.name}: ${strings.itemGone}`);
    });
  };

  // Что мешает оформить. Порядок проверок как на сервере: часы работы, зона, минимум.
  const blocker = ((): string | null => {
    if (isEmpty) return strings.cartEmpty;
    if (storeInfo && isStorePaused(storeInfo)) return strings.storePausedHint;
    if (storeInfo && !storeInfo.isOpen) return strings.storeClosedHint(localClock(storeInfo.opensAt, storeInfo.timezone));
    // Без адреса о зоне судить рано: нижняя панель просит его добавить.
    if (!address) return null;
    if (!address.deliverable) return strings.outOfZone;
    const missing = missingTo(tariff?.minOrderRsd, available);
    if (missing !== null) return strings.addMoreToOrder(formatPara(missing));
    const over = overweight(tariff, availableWeightG(cart));
    return over === null ? null : strings.orderTooHeavy(formatWeight(over.actual, lang), formatWeight(over.limit, lang));
  })();

  /** Самый свежий ещё не собранный заказ: в него можно добавить товары. */
  const addableOrder = watcher.active
    .filter((order) => order.status === 'PAID' || order.status === 'PICKING')
    .reduce<OrderListItem | null>((latest, order) => (latest === null || order.placedAt > latest.placedAt ? order : latest), null);

  const inCart = new Set(cart.lines.map((line) => line.productId));
  const recommendedItems = recommended.filter((item) => !inCart.has(item.id));
  const freeFrom = para(tariff?.freeFromRsd);
  const missingToFree = missingTo(tariff?.freeFromRsd, available);
  const eta = etaText(book, strings) ?? storeEtaText(storeInfo);

  const controls = hasGoneLines(cart) ? (
    // С закончившимся товаром оформление отклонил бы сервер: корзина правится одним нажатием.
    <BarButton onClick={() => applyCartChanges(pendingChanges(cartStore.get()))}>{strings.fixCart}</BarButton>
  ) : !address ? (
    // Нет адреса — нет склада и тарифа: сначала адрес, потом оформление.
    <BarButton onClick={openNewAddress}>{strings.addAddress}</BarButton>
  ) : outOfZone ? (
    <BarButton onClick={() => push({ kind: 'addresses' })}>{strings.changeAddress}</BarButton>
  ) : (
    <>
      {signedIn && addableOrder && hasAvailableLines && paymentConfig.cardToken && (
        <Button kind="secondary" onClick={() => setSupplementOrder(addableOrder)}>
          {strings.addToOrder(orderMoment(lang, addableOrder.placedAt))}
        </Button>
      )}
      <BarButton
        disabled={blocker !== null}
        onClick={() => {
          if (signedIn) push({ kind: 'checkout' });
          else {
            setPendingCheckout(true);
            openSignIn();
          }
        }}
      >
        <span>{strings.checkout}</span>
        <span className="spread" />
        <span className="t-numeric">{formatPara(total)}</span>
      </BarButton>
    </>
  );

  return (
    <div className="cart-steps">
      <ScreenPlaceContext value={{ root: false, covered: steps.length > 0, onBack: close }}>
        <Screen
          embedded
          closeIcon
          className="cart-screen is-first"
          {...(isEmpty ? {} : { actions: [{ icon: 'trash' as const, label: strings.clearCart, tone: 'danger' as const, onClick: clearCart }] })}
        >
          {isEmpty ? (
            <div className="cart-empty">
              <EmptyState image="/empty/cart.webp" title={strings.cartEmpty} hint={strings.cartEmptyHint} actionTitle={strings.backToCatalog} onAction={close} />
            </div>
          ) : (
            <>
              <div className="cart-scroll stack">
                <ScreenTitle text={strings.cartTitle} />

                {!address ? (
                  <button type="button" className="cart-notice is-row" onClick={openNewAddress}>
                    <Icon name="pin" size={20} className="cart-notice-icon" />
                    <b className="grow">{strings.addAddressForDelivery}</b>
                    <Icon name="chevronRight" size={14} weight={2.8} className="t-muted" />
                  </button>
                ) : (
                  tariff &&
                  freeFrom > 0n &&
                  hasAvailableLines && (
                    <div className="cart-notice">
                      <div className="row" style={{ gap: 8 }}>
                        <Icon name={missingToFree === null ? 'check' : 'bike'} size={20} className="cart-notice-icon" />
                        <b>{missingToFree === null ? strings.deliveryFreeUnlocked : strings.addMoreForFreeDelivery(formatPara(missingToFree))}</b>
                      </div>
                      <ProgressBar progress={ratio(available, freeFrom)} height={5} />
                    </div>
                  )
                )}

                <Card flush className="cart-lines">
                  {cart.lines.map((line) => (
                    <CartRow key={line.productId} line={line} cart={cart} strings={strings} onOpen={() => openLine(line)} />
                  ))}
                </Card>

                {recommendedItems.length > 0 ? (
                  <section className="cart-recommendations stack">
                    <h2 className="t-display">{strings.cartRecommendations}</h2>
                    <div className="lane snap product-lane">
                      {recommendedItems.map((item) => (
                        <ProductTile key={item.id} item={item} onOpen={openItem} />
                      ))}
                    </div>
                  </section>
                ) : (
                  recommendationsLoading && (
                    // Заглушка держит место ленты: итоги не должны прыгать, когда она появится.
                    <section className="cart-recommendations stack" aria-hidden="true">
                      <Skeleton width={180} height={24} radius={8} />
                      <div className="row" style={{ gap: 10 }}>
                        <Skeleton width={140} height={210} />
                        <Skeleton width={140} height={210} />
                        <Skeleton width={140} height={210} />
                      </div>
                    </section>
                  )
                )}

                {/* Итоги есть, только когда есть что купить и известен тариф. */}
                {hasAvailableLines && tariff && (
                  <Card>
                    <div className="stack" style={{ gap: 10 }}>
                      <TotalLine title={strings.itemsTotal} value={formatPara(available)} />
                      <TotalLine title={strings.deliveryFee} value={fee === 0n ? strings.deliveryFree : formatPara(fee)} highlighted={fee === 0n} />
                      {eta && <TotalLine title={strings.deliveryTime} value={eta} />}
                      <hr className="hairline" />
                      <div className="row cart-total">
                        <span className="t-display grow">{strings.total}</span>
                        <b className="t-numeric">{formatPara(total)}</b>
                      </div>
                      <NutritionSummary
                        amount={totalNutrition(
                          cart.lines
                            .filter((line) => !isGone(cart, line.productId))
                            .map((line) => ({ weightG: line.netWeightG, nutrition: line.nutrition, qty: line.qty })),
                        )}
                      />
                    </div>
                  </Card>
                )}
              </div>

              <div className="bottom-bar cart-bar">
                <div className="stack" style={{ gap: 10 }}>
                  {blocker && <p className="cart-blocker">{blocker}</p>}
                  {controls}
                </div>
              </div>
            </>
          )}
        </Screen>
      </ScreenPlaceContext>

      {steps.map((step, index) => (
        <ScreenPlaceContext key={`${step.kind}-${index}`} value={{ root: false, covered: index < steps.length - 1, onBack: pop }}>
          {step.kind === 'checkout' ? (
            <Checkout onChangeAddress={() => push({ kind: 'addresses' })} onBusy={onBusy} />
          ) : step.kind === 'addresses' ? (
            <AddressStep onDone={pop} onNew={openNewAddress} />
          ) : step.kind === 'newAddress' ? (
            <NewAddressScreen embedded onSaved={pop} />
          ) : (
            <ProductNavContext value={productNav}>
              <ProductScreen embedded item={step.item} />
            </ProductNavContext>
          )}
        </ScreenPlaceContext>
      ))}

      {supplementOrder && <SupplementSheet order={supplementOrder} onClose={() => setSupplementOrder(null)} />}
      {ageGate.pending && <AgeGateSheet key={ageGate.pending.id} request={ageGate.pending} onClose={dismissAgeGate} />}
    </div>
  );
}

/**
 * Плюс в корзине добавляет ту же строку: витрины здесь нет, корзине хватает полей строки.
 */
function stubItem(line: CartLine, cart: CartState): StorefrontItem {
  return {
    id: line.productId,
    name: line.name,
    categoryId: null,
    categoryName: null,
    unit: line.unit,
    sizeLabel: null,
    netWeightG: line.netWeightG,
    weightG: line.weightG,
    priceRsd: line.priceRsd,
    compareAtPriceRsd: null,
    promoUntil: null,
    vatRateBp: 0,
    photoUrl: line.photoUrl,
    videoUrl: null,
    photos: null,
    availableQty: cart.availability[line.productId] ?? line.qty + 1,
    ean: null,
    description: null,
    composition: null,
    countryOfOrigin: null,
    allergens: null,
    additives: null,
    shelfLifeDays: null,
    storage: null,
    isNew: false,
    ageRestricted: line.ageRestricted,
    nutrition: line.nutrition ?? { energyKcal: null, proteinG: null, fatG: null, carbsG: null },
    tags: { vegan: false, glutenFree: false, sugarFree: false },
  };
}

function CartRow({ line, cart, strings, onOpen }: { line: CartLine; cart: CartState; strings: Strings; onOpen: () => void }) {
  const gone = isGone(cart, line.productId);
  const left = cart.availability[line.productId];
  return (
    <div className={cx('cart-row', gone && 'is-gone')}>
      <button type="button" className="cart-row-photo" aria-label={line.name} onClick={onOpen}>
        <RemoteImage src={line.photoUrl} />
      </button>
      <div className="grow stack cart-row-text">
        <button type="button" className="cart-row-name" onClick={onOpen}>
          {line.name}
        </button>
        {/* Цена закончившегося товара зачёркнута: в итоги она не входит. */}
        <b className="t-numeric cart-row-price">{formatPara(lineTotal(line))}</b>
        {gone ? (
          <span className="t-caption t-muted">{strings.outOfStock}</span>
        ) : (
          left !== undefined && left < line.qty && <span className="t-caption cart-row-left">{strings.itemLeft(left)}</span>
        )}
      </div>
      {gone ? (
        <button type="button" className="text-button" onClick={() => removeFromCart(line.productId)}>
          {strings.delete}
        </button>
      ) : (
        <QuantityStepper
          compact
          quantity={line.qty}
          canIncrease={canAdd(cart, line.productId)}
          onDecrease={() => decreaseInCart(line.productId)}
          onIncrease={() => addWithChecks(stubItem(line, cart))}
        />
      )}
    </div>
  );
}

function TotalLine({ title, value, highlighted }: { title: string; value: string; highlighted?: boolean }) {
  return (
    <div className="row">
      <span className="t-subheadline t-muted grow">{title}</span>
      <span className={cx('t-numeric', highlighted && 'checkout-accent')}>{value}</span>
    </div>
  );
}

/**
 * Выбор адреса доставки шагом листа. Адрес определяет склад, а с ним витрину и тариф.
 * Новый адрес заводится на карте, поэтому за ним лист закрывается.
 */
function AddressStep({ onDone, onNew }: { onDone: () => void; onNew: () => void }) {
  const strings = useStrings();
  const book = useAddressBook();
  const [errorText, setErrorText] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  const selectedId = selectedAddress(book)?.id;
  // Сохранённые адреса или точка гостя, пока аккаунта нет.
  const rows: MyAddress[] = book.addresses.length > 0 ? book.addresses : book.guest ? [{ id: GUEST_ADDRESS_ID, ...book.guest, deliverable: true }] : [];

  const select = async (address: MyAddress) => {
    try {
      if (address.id !== GUEST_ADDRESS_ID) await selectAndRefresh(address.id);
      onDone();
    } catch (error) {
      setErrorText(textOf(strings, error));
    }
  };

  const remove = async (address: MyAddress) => {
    setRemoving(address.id);
    try {
      if (address.id === GUEST_ADDRESS_ID) setGuest(null);
      else await removeAddress(address.id);
    } catch (error) {
      setErrorText(textOf(strings, error));
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Screen embedded className="cart-screen">
      <div className="cart-scroll readable stack">
        <ScreenTitle text={strings.myAddresses} />

        {rows.length > 0 && (
          <Card flush className="cart-lines">
            {rows.map((address) => {
              const selected = address.id === selectedId;
              const details = addressDetailsLine(address, strings);
              return (
                <div key={address.id} className="cart-address-row">
                  <button type="button" className="cart-address-row-main" disabled={!address.deliverable} onClick={() => void select(address)}>
                    <Icon name={selected ? 'check' : 'pin'} size={22} className={selected ? 'checkout-accent' : 't-muted'} />
                    <span className="grow stack">
                      <span className={cx('cart-address-row-text', !address.deliverable && 't-muted')}>{address.text}</span>
                      {details && <span className="t-footnote t-muted">{details}</span>}
                      {address.comment && <span className="t-footnote t-muted">{address.comment}</span>}
                      {!address.deliverable && <span className="t-caption cart-address-row-off">{strings.notDeliverable}</span>}
                    </span>
                  </button>
                  <button type="button" className="cart-address-row-remove" aria-label={strings.delete} disabled={removing !== null} onClick={() => void remove(address)}>
                    {removing === address.id ? <Spinner size={14} /> : <Icon name="trash" size={15} weight={2.2} />}
                  </button>
                </div>
              );
            })}
          </Card>
        )}

        <button type="button" className="cart-address-new" onClick={onNew}>
          <Icon name="plus" size={20} weight={2.6} />
          <b>{strings.newAddress}</b>
        </button>

        {errorText && <ErrorBanner text={errorText} />}
      </div>
    </Screen>
  );
}
